import React, { useEffect, useRef, useState } from 'react';
import { ApiErrorCustom, fetchRecordByUuid } from '../api';
import { ServiceRecord, ServiceRecordPatchDto, ServiceRecordStatus, STATUS_MAP, VALID_SERVICE_STATUSES } from '../types';
import { formatDate, formatPricePLN, parsePLNToCents } from '../utils/formatters';
import { ClientMessageSection } from './ClientMessageSection';
import { CommunicationHistorySection } from './CommunicationHistorySection';
import { PhotoGallerySection } from './PhotoGallerySection';

interface RecordDetailsProps {
  record: ServiceRecord;
  onSavePatch: (uuid: string, patch: ServiceRecordPatchDto) => Promise<void>;
  onToast: (msg: string, type?: 'info' | 'success' | 'error') => void;
  isSaving: boolean;
  onBackToList?: () => void;
}

export type EditableSection = 'client' | 'device' | 'billing' | 'notes' | 'description' | null;

export const RecordDetails: React.FC<RecordDetailsProps> = ({
  record,
  onSavePatch,
  onToast,
  isSaving,
  onBackToList,
}) => {
  const messageSectionRef = useRef<HTMLDivElement>(null);

  // Active section currently in edit mode (only one at a time)
  const [editingSection, setEditingSection] = useState<EditableSection>(null);

  // Form State
  const [clientName, setClientName] = useState(record.clientName);
  const [clientPhone, setClientPhone] = useState(record.clientPhone);
  const [deviceType, setDeviceType] = useState(record.deviceType);
  const [brand, setBrand] = useState(record.brand || '');
  const [model, setModel] = useState(record.model || '');
  const [serialNumber, setSerialNumber] = useState(record.serialNumber || '');
  const [description, setDescription] = useState(record.description || '');
  const [visualCondition, setVisualCondition] = useState(record.visualCondition || '');
  const [status, setStatus] = useState<ServiceRecordStatus>(record.status);
  const [pricePlnText, setPricePlnText] = useState(
    record.priceCents != null ? (record.priceCents / 100).toFixed(2).replace('.', ',') : ''
  );
  const [notes, setNotes] = useState(record.notes || '');

  // Section Error & OCC Conflict State
  const [sectionError, setSectionError] = useState<{ section: string; message: string } | null>(null);
  const [sectionConflict, setSectionConflict] = useState<string | null>(null);
  const [commRefreshCounter, setCommRefreshCounter] = useState(0);

  // Sync state when selected record changes
  useEffect(() => {
    setClientName(record.clientName);
    setClientPhone(record.clientPhone);
    setDeviceType(record.deviceType);
    setBrand(record.brand || '');
    setModel(record.model || '');
    setSerialNumber(record.serialNumber || '');
    setDescription(record.description || '');
    setVisualCondition(record.visualCondition || '');
    setStatus(record.status);
    setPricePlnText(
      record.priceCents != null ? (record.priceCents / 100).toFixed(2).replace('.', ',') : ''
    );
    setNotes(record.notes || '');
    setSectionError(null);
    setSectionConflict(null);
    setEditingSection(null);
  }, [record]);

  const handleCopyPhone = async () => {
    try {
      await navigator.clipboard.writeText(record.clientPhone);
      onToast(`Skopiowano numer ${record.clientPhone} do schowka`, 'success');
    } catch (e) {
      onToast('Błąd kopiowania numeru do schowka', 'error');
    }
  };

  const scrollToMessage = () => {
    messageSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleQuickStatusChange = async (newStatus: ServiceRecordStatus) => {
    if (newStatus === record.status) return;
    const patchDto: ServiceRecordPatchDto = {
      expectedRevision: record.revision,
      status: newStatus,
    };
    try {
      await onSavePatch(record.uuid, patchDto);
      const statusName = STATUS_MAP[newStatus]?.label || newStatus;
      onToast(`Zmieniono status na: ${statusName}`, 'success');
      setCommRefreshCounter((c) => c + 1);
    } catch (err: any) {
      if (err instanceof ApiErrorCustom && (err.status === 409 || err.code === 'REVISION_CONFLICT')) {
        onToast('Konflikt wersji: Zgłoszenie zostało zmodyfikowane w innym miejscu.', 'error');
      } else {
        onToast('Nie udało się zmienić statusu.', 'error');
      }
    }
  };

  const isSectionDirty = (section: EditableSection): boolean => {
    if (!section) return false;
    switch (section) {
      case 'client':
        return clientName !== record.clientName || clientPhone !== record.clientPhone;
      case 'device':
        return (
          deviceType !== record.deviceType ||
          brand !== (record.brand || '') ||
          model !== (record.model || '') ||
          serialNumber !== (record.serialNumber || '')
        );
      case 'billing': {
        const origCents = record.priceCents == null ? null : record.priceCents;
        const newCents = pricePlnText.trim() === '' ? null : parsePLNToCents(pricePlnText);
        return status !== record.status || newCents !== origCents;
      }
      case 'notes':
        return notes !== (record.notes || '');
      case 'description':
        return (
          description !== (record.description || '') ||
          visualCondition !== (record.visualCondition || '')
        );
      default:
        return false;
    }
  };

  const handleStartSectionEdit = (section: NonNullable<EditableSection>) => {
    if (editingSection && editingSection !== section) {
      if (isSectionDirty(editingSection)) {
        const confirmed = window.confirm(
          'Masz niezapisane zmiany w innej karcie. Czy chcesz je anulować i przejść do edycji tej karty?'
        );
        if (!confirmed) return;
      }
      // Revert previous section
      handleResetFieldsToRecord();
    }
    setEditingSection(section);
    setSectionError(null);
    setSectionConflict(null);
  };

  const handleResetFieldsToRecord = () => {
    setClientName(record.clientName);
    setClientPhone(record.clientPhone);
    setDeviceType(record.deviceType);
    setBrand(record.brand || '');
    setModel(record.model || '');
    setSerialNumber(record.serialNumber || '');
    setDescription(record.description || '');
    setVisualCondition(record.visualCondition || '');
    setStatus(record.status);
    setPricePlnText(
      record.priceCents != null ? (record.priceCents / 100).toFixed(2).replace('.', ',') : ''
    );
    setNotes(record.notes || '');
  };

  const handleCancelSectionEdit = () => {
    handleResetFieldsToRecord();
    setEditingSection(null);
    setSectionError(null);
    setSectionConflict(null);
  };

  const handleRefreshCurrentData = async () => {
    try {
      const fresh = await fetchRecordByUuid(record.uuid);
      setClientName(fresh.clientName);
      setClientPhone(fresh.clientPhone);
      setDeviceType(fresh.deviceType);
      setBrand(fresh.brand || '');
      setModel(fresh.model || '');
      setSerialNumber(fresh.serialNumber || '');
      setDescription(fresh.description || '');
      setVisualCondition(fresh.visualCondition || '');
      setStatus(fresh.status);
      setPricePlnText(
        fresh.priceCents != null ? (fresh.priceCents / 100).toFixed(2).replace('.', ',') : ''
      );
      setNotes(fresh.notes || '');
      setSectionConflict(null);
      setSectionError(null);
      onToast(
        `Pobrano aktualną wersję z serwera (rewizja #${fresh.revision}). Sprawdź dane przed zapisem.`,
        'info'
      );
    } catch (e) {
      onToast('Nie udało się pobrać aktualnych danych z serwera', 'error');
    }
  };

  const handleSaveSection = async (section: NonNullable<EditableSection>, e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setSectionError(null);
    setSectionConflict(null);

    const patchDto: ServiceRecordPatchDto = {
      expectedRevision: record.revision,
    };

    if (section === 'client') {
      if (!clientPhone.trim()) {
        setSectionError({ section, message: 'Numer telefonu klienta jest wymagany' });
        return;
      }
      patchDto.clientName = clientName.trim();
      patchDto.clientPhone = clientPhone.trim();
    } else if (section === 'device') {
      if (!deviceType.trim()) {
        setSectionError({ section, message: 'Typ urządzenia jest wymagany' });
        return;
      }
      patchDto.deviceType = deviceType.trim();
      patchDto.brand = brand.trim();
      patchDto.model = model.trim();
      patchDto.serialNumber = serialNumber.trim();
    } else if (section === 'billing') {
      patchDto.status = status;
      const trimmed = pricePlnText.trim();
      patchDto.priceCents = trimmed === '' ? null : parsePLNToCents(trimmed);
    } else if (section === 'notes') {
      patchDto.notes = notes.trim();
    } else if (section === 'description') {
      patchDto.description = description.trim();
      patchDto.visualCondition = visualCondition.trim();
    }

    try {
      await onSavePatch(record.uuid, patchDto);
      setEditingSection(null);
      setSectionError(null);
      setSectionConflict(null);
      onToast('Zapisano zmiany w sekcji', 'success');
      setCommRefreshCounter((c) => c + 1);
    } catch (err: any) {
      if (err instanceof ApiErrorCustom) {
        if (err.status === 409 || err.code === 'REVISION_CONFLICT') {
          setSectionConflict(section);
        } else if (err.status === 400 || err.code === 'VALIDATION_ERROR') {
          setSectionError({ section, message: err.message || 'Niepoprawne dane w formularzu.' });
        } else if (err.status === 401 || err.status === 403) {
          setSectionError({ section, message: 'Brak uprawnień lub wygasła sesja Cloudflare Access.' });
        } else {
          setSectionError({ section, message: err.message || 'Błąd zapisu danych na serwerze.' });
        }
      } else {
        setSectionError({ section, message: err.message || 'Nie udało się zapisać zmian. Spróbuj ponownie.' });
      }
    }
  };

  const statusInfo = STATUS_MAP[record.status] || { label: record.status, color: '#000', bgColor: '#eee' };
  const deviceTitle = [record.brand, record.model].filter(Boolean).join(' ') || record.deviceType;

  // Render Pencil Button Helper
  const renderPencilButton = (section: NonNullable<EditableSection>, title: string) => {
    if (editingSection === section) return null;
    return (
      <button
        type="button"
        className="btn btn-icon-subtle"
        onClick={() => handleStartSectionEdit(section)}
        title={title}
        aria-label={title}
      >
        ✏️ Edytuj
      </button>
    );
  };

  // Render Section Action Footer Helper
  const renderSectionFooter = (section: NonNullable<EditableSection>) => {
    if (editingSection !== section) return null;
    return (
      <div style={{ marginTop: '14px', borderTop: '1px solid var(--border-light)', paddingTop: '10px' }}>
        {sectionConflict === section && (
          <div
            style={{
              backgroundColor: '#fefce8',
              border: '1px solid #eab308',
              borderRadius: '6px',
              padding: '10px 12px',
              marginBottom: '10px',
              color: '#854d0e',
              fontSize: '0.85rem',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '6px' }}>
              ⚠️ Zgłoszenie zostało zmodyfikowane na innym urządzeniu.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className="btn btn-warning btn-sm"
                onClick={handleRefreshCurrentData}
                disabled={isSaving}
                style={{ fontSize: '0.8rem', padding: '3px 8px' }}
              >
                🔄 Pobierz aktualne dane
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleCancelSectionEdit}
                disabled={isSaving}
                style={{ fontSize: '0.8rem', padding: '3px 8px' }}
              >
                Anuluj
              </button>
            </div>
          </div>
        )}

        {sectionError?.section === section && !sectionConflict && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #ef4444',
              borderRadius: '6px',
              padding: '8px 12px',
              marginBottom: '10px',
              color: '#991b1b',
              fontSize: '0.85rem',
              fontWeight: 500,
            }}
          >
            ⛔ {sectionError.message}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleCancelSectionEdit}
            disabled={isSaving}
            style={{ fontSize: '0.825rem', padding: '4px 12px' }}
          >
            Anuluj
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => handleSaveSection(section)}
            disabled={isSaving}
            style={{ fontSize: '0.825rem', padding: '4px 14px' }}
          >
            {isSaving ? 'Zapisywanie...' : '💾 Zapisz'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="detail-panel">
      {/* Mobile Back Navigation Button */}
      {onBackToList && (
        <div className="mobile-back-bar">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onBackToList}>
            ← Powrót do listy zgłoszeń
          </button>
        </div>
      )}

      {/* Sticky Action Header Card */}
      <div className="sticky-header-card">
        <div className="header-meta-row">
          <div className="header-title-group">
            <h2 className="header-ticket-title" style={{ margin: 0 }}>{record.ticketNumber}</h2>
          </div>

          <div className="header-action-group">
            {/* Status Dropdown Quick Selector */}
            <div className="header-status-select-container" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label htmlFor="header-status-select" style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                Status:
              </label>
              <select
                id="header-status-select"
                className="form-control header-status-select"
                value={record.status}
                disabled={isSaving}
                onChange={(e) => handleQuickStatusChange(e.target.value as ServiceRecordStatus)}
                style={{
                  padding: '4px 10px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  borderRadius: '6px',
                  border: `1px solid ${statusInfo.color}`,
                  backgroundColor: statusInfo.bgColor,
                  color: statusInfo.color,
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  outline: 'none',
                }}
              >
                {VALID_SERVICE_STATUSES.map((st) => (
                  <option key={st} value={st} style={{ backgroundColor: '#ffffff', color: '#1e293b' }}>
                    {STATUS_MAP[st]?.label || st}
                  </option>
                ))}
              </select>
            </div>

            <button type="button" className="btn btn-copy" onClick={handleCopyPhone}>
              📞 Kopiuj numer
            </button>
            <button type="button" className="btn btn-secondary" onClick={scrollToMessage}>
              💬 Wiadomość dla klienta
            </button>
          </div>
        </div>

        <div className="header-sub-info">
          <span>Klient: <strong>{record.clientName}</strong> ({record.clientPhone})</span>
          <span>Urządzenie: <strong>{deviceTitle}</strong></span>
          <span>Cena: <strong>{formatPricePLN(record.priceCents)}</strong></span>
        </div>
      </div>

      {/* Main Details Section Grid (2 Columns on Desktop, 1 Column on Mobile) */}
      <div className="details-grid">
        {/* LEFT COLUMN */}
        <div className="details-column">
          {/* Card 1: Dane Klienta */}
          <div className="card-container" style={{ border: editingSection === 'client' ? '1px solid var(--primary, #2563eb)' : undefined }}>
            <div className="card-title">
              <span>👤 Dane Klienta</span>
              {renderPencilButton('client', 'Edytuj dane klienta')}
            </div>
            {editingSection === 'client' ? (
              <div className="field-grid">
                <div className="field-group">
                  <label className="field-label" htmlFor="client-name-input">Imię i Nazwisko / Firma</label>
                  <input
                    id="client-name-input"
                    type="text"
                    className="field-input"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="field-group">
                  <label className="field-label" htmlFor="client-phone-input">Telefon kontaktowy</label>
                  <input
                    id="client-phone-input"
                    type="text"
                    className="field-input"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    required
                  />
                </div>
              </div>
            ) : (
              <div className="field-grid">
                {record.clientName && record.clientName.trim().length > 0 && (
                  <div className="field-group">
                    <label className="field-label">Imię i Nazwisko / Firma</label>
                    <div className="field-value">{record.clientName}</div>
                  </div>
                )}
                {record.clientPhone && record.clientPhone.trim().length > 0 && (
                  <div className="field-group">
                    <label className="field-label">Telefon kontaktowy</label>
                    <div className="field-value">{record.clientPhone}</div>
                  </div>
                )}
              </div>
            )}
            {renderSectionFooter('client')}
          </div>

          {/* Card 2: Urządzenie i Sprzęt */}
          <div className="card-container" style={{ border: editingSection === 'device' ? '1px solid var(--primary, #2563eb)' : undefined }}>
            <div className="card-title">
              <span>💻 Urządzenie i Sprzęt</span>
              {renderPencilButton('device', 'Edytuj urządzenie i sprzęt')}
            </div>
            {editingSection === 'device' ? (
              <div className="field-grid">
                <div className="field-group">
                  <label className="field-label" htmlFor="device-type-input">Typ urządzenia</label>
                  <input
                    id="device-type-input"
                    type="text"
                    className="field-input"
                    value={deviceType}
                    onChange={(e) => setDeviceType(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="field-group">
                  <label className="field-label" htmlFor="device-brand-input">Marka</label>
                  <input
                    id="device-brand-input"
                    type="text"
                    className="field-input"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                  />
                </div>
                <div className="field-group">
                  <label className="field-label" htmlFor="device-model-input">Model</label>
                  <input
                    id="device-model-input"
                    type="text"
                    className="field-input"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                  />
                </div>
                <div className="field-group">
                  <label className="field-label" htmlFor="device-sn-input">Numer seryjny (S/N)</label>
                  <input
                    id="device-sn-input"
                    type="text"
                    className="field-input"
                    value={serialNumber}
                    onChange={(e) => setSerialNumber(e.target.value)}
                    placeholder="Brak"
                  />
                </div>
              </div>
            ) : (
              <div className="field-grid">
                {record.deviceType && record.deviceType.trim().length > 0 && (
                  <div className="field-group">
                    <label className="field-label">Typ urządzenia</label>
                    <div className="field-value">{record.deviceType}</div>
                  </div>
                )}
                {record.brand && record.brand.trim().length > 0 && (
                  <div className="field-group">
                    <label className="field-label">Marka</label>
                    <div className="field-value">{record.brand}</div>
                  </div>
                )}
                {record.model && record.model.trim().length > 0 && (
                  <div className="field-group">
                    <label className="field-label">Model</label>
                    <div className="field-value">{record.model}</div>
                  </div>
                )}
                {record.serialNumber && record.serialNumber.trim().length > 0 && (
                  <div className="field-group">
                    <label className="field-label">Numer seryjny (S/N)</label>
                    <div className="field-value">{record.serialNumber}</div>
                  </div>
                )}
              </div>
            )}
            {renderSectionFooter('device')}
          </div>

          {/* Card 3: Status & Rozliczenie */}
          <div className="card-container" style={{ border: editingSection === 'billing' ? '1px solid var(--primary, #2563eb)' : undefined }}>
            <div className="card-title">
              <span>🛠️ Status & Rozliczenie</span>
              {renderPencilButton('billing', 'Edytuj status i cenę serwisu')}
            </div>
            {editingSection === 'billing' ? (
              <div className="field-grid">
                <div className="field-group">
                  <label className="field-label" htmlFor="card-status-select">Status zlecenia</label>
                  <select
                    id="card-status-select"
                    className="field-input"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as ServiceRecordStatus)}
                    disabled={isSaving}
                  >
                    {VALID_SERVICE_STATUSES.map((st) => (
                      <option key={st} value={st}>
                        {STATUS_MAP[st]?.label || st}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field-group">
                  <label className="field-label" htmlFor="card-price-input">Cena serwisu (PLN)</label>
                  <input
                    id="card-price-input"
                    type="text"
                    className="field-input"
                    value={pricePlnText}
                    onChange={(e) => setPricePlnText(e.target.value)}
                    placeholder="np. 200 lub 200,50 (puste = brak wyceny)"
                    disabled={isSaving}
                    autoFocus
                  />
                </div>
              </div>
            ) : (
              <div className="field-grid">
                <div className="field-group">
                  <label className="field-label">Status zlecenia</label>
                  <div className="field-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="status-pill" style={{ color: statusInfo.color, backgroundColor: statusInfo.bgColor }}>
                      {statusInfo.label}
                    </span>
                  </div>
                </div>
                <div className="field-group">
                  <label className="field-label">Cena serwisu</label>
                  <div
                    className="field-value"
                    style={{
                      fontWeight: 700,
                      color: record.priceCents == null ? 'var(--text-muted)' : 'var(--primary)',
                      fontSize: '1.05rem',
                    }}
                  >
                    {formatPricePLN(record.priceCents)}
                  </div>
                </div>
              </div>
            )}
            {renderSectionFooter('billing')}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="details-column">
          {/* Card 4: Notatka Wewnętrzna Serwisu */}
          <div
            className="card-container note-card"
            style={{
              border: editingSection === 'notes' ? '1px solid #ca8a04' : undefined,
            }}
          >
            <div className="card-title" style={{ color: '#854d0e' }}>
              <div>
                <span>📌 Notatka Wewnętrzna Serwisu</span>{' '}
                <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#a16207' }}>(niewidoczna dla klienta)</span>
              </div>
              {renderPencilButton('notes', 'Edytuj notatkę wewnętrzną')}
            </div>
            <div className="field-group full-width">
              {editingSection === 'notes' ? (
                <textarea
                  id="notes-input"
                  className="field-input"
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Wpisz uwagi wewnętrzne, kody PIN, notatki techniczne..."
                  style={{ width: '100%', resize: 'vertical' }}
                  autoFocus
                />
              ) : (
                <div className="internal-note-box">
                  {record.notes && record.notes.trim().length > 0 ? record.notes : 'Brak notatek wewnętrznych.'}
                </div>
              )}
            </div>
            {renderSectionFooter('notes')}
          </div>

          {/* Card 5: Opis Zgłoszenia i Stan */}
          <div className="card-container" style={{ border: editingSection === 'description' ? '1px solid var(--primary, #2563eb)' : undefined }}>
            <div className="card-title">
              <span>📋 Opis Zgłoszenia i Stan</span>
              {renderPencilButton('description', 'Edytuj opis zgłoszenia i stan')}
            </div>
            {editingSection === 'description' ? (
              <div className="field-grid-desc">
                <div className="field-group full-width">
                  <label className="field-label" htmlFor="desc-issue-input">Opis usterki</label>
                  <textarea
                    id="desc-issue-input"
                    className="field-input"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    style={{ width: '100%', resize: 'vertical' }}
                    autoFocus
                  />
                </div>
                <div className="field-group full-width">
                  <label className="field-label" htmlFor="desc-visual-input">Stan wizualny przy przyjęciu</label>
                  <textarea
                    id="desc-visual-input"
                    className="field-input"
                    rows={2}
                    value={visualCondition}
                    onChange={(e) => setVisualCondition(e.target.value)}
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </div>
              </div>
            ) : (
              <div className="field-grid-desc">
                {record.description && record.description.trim().length > 0 && (
                  <div className="field-group">
                    <label className="field-label">Opis usterki</label>
                    <div className="field-value" style={{ whiteSpace: 'pre-wrap' }}>{record.description}</div>
                  </div>
                )}
                {record.visualCondition && record.visualCondition.trim().length > 0 && (
                  <div className="field-group">
                    <label className="field-label">Stan wizualny przy przyjęciu</label>
                    <div className="field-value" style={{ whiteSpace: 'pre-wrap' }}>{record.visualCondition}</div>
                  </div>
                )}
              </div>
            )}
            {renderSectionFooter('description')}
          </div>

          {/* Card 6: Synchronizacja Centralna (CRM) & Daty */}
          <div className="card-container">
            <div className="card-title">🔄 Synchronizacja & Daty</div>
            <div className="field-grid sync-field-grid">
              <div className="field-group">
                <label className="field-label">Data przyjęcia</label>
                <div className="field-value">{formatDate(record.createdAt)}</div>
              </div>
              {(record.completedAt || record.handedOverAt) ? (
                <div className="field-group">
                  <label className="field-label">Zakończono / Wydano</label>
                  <div className="field-value">{formatDate(record.completedAt || record.handedOverAt)}</div>
                </div>
              ) : null}
              <div className="field-group">
                <label className="field-label">Wersja (Revision)</label>
                <div className="field-value" style={{ fontFamily: 'var(--font-mono)' }}>#{record.revision}</div>
              </div>
              <div className="field-group">
                <label className="field-label">Baza danych D1</label>
                <div className="field-value" style={{ color: 'var(--success)', fontWeight: 600 }}>Zsynchronizowano</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 5: Photo Gallery Section (Full Width) */}
      <PhotoGallerySection recordUuid={record.uuid} onToast={onToast} />

      {/* Section 6: Client Message Generator & Communication History (Full Width) */}
      <div ref={messageSectionRef}>
        <ClientMessageSection
          record={record}
          onCopySuccess={(msg) => onToast(msg, 'success')}
          onCommunicationAdded={() => setCommRefreshCounter((prev) => prev + 1)}
        />
        <CommunicationHistorySection recordUuid={record.uuid} refreshTrigger={commRefreshCounter} />
      </div>
    </div>
  );
};
